import { Post } from '@/lib/supabase';

interface PostListProps {
  posts: Post[];
  onEdit: (post: Post) => void;
  onDelete: (id: string) => void;
  onTogglePublish: (post: Post) => void;
}

const categoryLabels: Record<string, string> = {
  web: 'WEB制作',
  sns: 'SNS運用',
  seo: 'SEO・MEO',
  marketing: 'マーケティング',
  ai: 'AI活用',
};

const categoryColors: Record<string, string> = {
  web: 'bg-blue-50 text-blue-600',
  sns: 'bg-cyan-50 text-cyan-600',
  seo: 'bg-green-50 text-green-600',
  marketing: 'bg-orange-50 text-orange-600',
  ai: 'bg-violet-50 text-violet-600',
};

export default function PostList({ posts, onEdit, onDelete, onTogglePublish }: PostListProps) {
  if (posts.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-[#F5F7FA]">
          <i className="ri-article-line text-2xl text-[#ccc]" />
        </div>
        <p className="text-[#999] text-sm">記事がまだありません</p>
        <p className="text-[#bbb] text-xs mt-1">「新規作成」から最初の記事を書いてみましょう</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-[#F0F4FF]">
      {posts.map((post) => (
        <div key={post.id} className="flex items-center gap-4 py-4 px-2 hover:bg-[#FAFBFF] transition-colors rounded-lg group">
          {/* Cover */}
          <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-[#F5F7FA]">
            {post.cover_image ? (
              <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover object-top" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <i className="ri-image-line text-[#ccc] text-xl" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${categoryColors[post.category] ?? 'bg-gray-50 text-gray-600'}`}>
                {categoryLabels[post.category] ?? post.category}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${post.published ? 'bg-emerald-50 text-emerald-600' : 'bg-[#F5F7FA] text-[#999]'}`}>
                {post.published ? '公開中' : '下書き'}
              </span>
            </div>
            <p className="text-sm font-semibold text-[#0A1628] line-clamp-1">{post.title}</p>
            <p className="text-xs text-[#999] mt-0.5">
              {post.published_at ? new Date(post.published_at).toLocaleDateString('ja-JP') : '未設定'}
              　{post.reading_time}分で読める
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            <button
              onClick={() => onTogglePublish(post)}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                post.published
                  ? 'bg-[#F5F7FA] text-[#666] hover:bg-[#E8F0FF] hover:text-[#1E6FFF]'
                  : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
              }`}
            >
              {post.published ? '非公開にする' : '公開する'}
            </button>
            <button
              onClick={() => onEdit(post)}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#F5F7FA] text-[#666] hover:bg-[#E8F0FF] hover:text-[#1E6FFF] transition-all cursor-pointer"
            >
              <i className="ri-edit-line text-sm" />
            </button>
            <button
              onClick={() => onDelete(post.id)}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#F5F7FA] text-[#999] hover:bg-red-50 hover:text-red-500 transition-all cursor-pointer"
            >
              <i className="ri-delete-bin-line text-sm" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
